import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, PenLine } from "lucide-react";
import { classNames } from "../../helpers/classNames";
import RetryImage from "../ui/RetryImage";

/**
 * The Preview step's page viewer (docs/design/HANDOFF.md, "Preview": Preview,
 * Tab5Preview, Desk5Preview and the loading, failed and Etsy versions).
 *
 * On every device the book shows as it opens: the cover alone, then spreads
 * of facing pages (pages 1 and 2 of `pages`, 3 and 4, ...). The parent passes
 * the pages in print order, blanks included (the inside front cover when
 * there's no dedication, a blank page closing a half-filled last spread).
 * Arrows and a swipe move a spread at a time; "Pages 2 and 3 of N" counts photo
 * pages only (decided 2026-10-23). The thumbnail strip groups the spreads with
 * a gap between them. Pages aren't tappable (decided 2026-10-23: no
 * enlarged view).
 *
 * `pages`: [{ id, label, url, ready, isCover, content, failed, blank, dedication }].
 * `dedication` (with `blank`): the dedication page of a book that has none yet,
 * drawn with Page photos' dedication tile styling (the pen and "Dedication page", no border).
 * `onOpen`: the page is a button (the empty dedication page goes to step 4's dedication editor).
 * `action`: { label, ariaLabel, icon, onClick, below } a pill on the page, centred in its bottom
 * margin, or just below the spread when `below` (the dedication's "Read up close", decided
 * 2026-10-26: the one page on Preview that opens; the others stay non-tappable).
 * A page can carry `content` (a React node: the dedication, a failure block)
 * instead of a `url`; `failed` gives its thumbnail a red border; `blank` is a
 * page that prints empty. A page that isn't ready shows a pulsing placeholder
 * and "Drawing page N". Coloring pages get a CSS watermark (the handoff asks
 * for server-side watermarked previews; this stands in for the screen).
 */

/** A page's pill button (`page.action`): white, rose text; the tap area is 44px tall. */
const PagePill = ({ action }) => (
  <button type="button" onClick={action.onClick} aria-label={action.ariaLabel || action.label} className="group flex h-11 items-center focus:outline-none">
    <span className="flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-full border border-[#E5C5CB] bg-white px-3 text-xs font-bold text-md-pink-dark shadow-[0_2px_8px_rgba(27,13,18,0.16)] group-focus-visible:ring-2 group-focus-visible:ring-md-pink md:h-[34px] md:px-3.5 md:text-[13px]">
      {action.icon}
      {action.label}
    </span>
  </button>
);

/** One page of a spread. `side` puts its label in the outer corner. */
const Page = ({ page, side = "left" }) => {
  const body = page?.blank && page.dedication ? (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-white p-2 text-center text-md-pink-dark">
      <PenLine className="h-6 w-6 md:h-7 md:w-7" aria-hidden="true" />
      <span className="text-xs font-semibold leading-tight text-md-muted md:text-sm">{page.label}</span>
      <span className="-mt-1 text-[11px] leading-tight text-md-muted md:text-xs">(optional)</span>
    </div>
  ) : page?.blank ? (
    <div className="absolute inset-0 flex items-center justify-center bg-white p-2 text-center text-xs text-[#8A8284]">{page.label}</div>
  ) : page?.ready && (page.url || page.content) ? (
    <>
      {page.content || <RetryImage src={page.url} alt={page.label} className="h-full w-full object-contain" />}
      {!page.isCover && !page.content ? <Watermark /> : null}
    </>
  ) : page ? (
    <div role="status" className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[#F6F2EC] p-2 text-center">
      <span aria-hidden="true" className="h-[52%] w-[62%] animate-pulse rounded-lg bg-[#ECE5DC]" />
      <span className="flex items-center gap-1.5 text-xs font-semibold text-md-muted md:text-sm">
        <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-md-pink-dark" aria-hidden="true" />
        Drawing {page.label.toLowerCase()}
      </span>
    </div>
  ) : null;

  return (
    <div className={classNames("relative aspect-[175/232] min-w-0 flex-1 overflow-hidden bg-white", side === "left" && "border-r border-md-pink-light-3")}>
      {page?.onOpen ? (
        <button
          type="button"
          onClick={page.onOpen}
          aria-label={page.openLabel || page.label}
          className="absolute inset-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-md-pink"
        >
          {body}
        </button>
      ) : (
        body
      )}
      {page?.action && !page.action.below ? (
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 md:bottom-2">
          <PagePill action={page.action} />
        </div>
      ) : null}
      {page && !page.blank && !page.failed && !page.action ? (
        <span className={classNames("pointer-events-none absolute bottom-1.5 text-[11px] text-md-muted md:bottom-2.5 md:text-xs", side === "left" ? "left-2 md:left-3" : "right-2 md:right-3")}>
          {page.label}
        </span>
      ) : null}
    </div>
  );
};

/** Indexes of `pages` grouped as the open book: [0], [1, 2], [3, 4], ... */
export function bookSpreads(total) {
  const spreads = total ? [[0]] : [];
  for (let i = 1; i < total; i += 2) spreads.push(i + 1 < total ? [i, i + 1] : [i]);
  return spreads;
}

/** "Cover", "Dedication and page 1", "Page 1 of 20", "Pages 2 and 3 of 20". */
export function spreadLabel(spreadPages, photoPageCount) {
  if (spreadPages.some((p) => p.isCover)) return "Cover";
  const numbers = spreadPages.filter((p) => p.label?.startsWith("Page ")).map((p) => Number(p.label.slice(5)));
  if (spreadPages.some((p) => p.label === "Dedication")) return numbers.length ? `Dedication and page ${numbers[0]}` : "Dedication";
  if (!numbers.length) return spreadPages[0]?.label || "";
  return numbers.length === 1 ? `Page ${numbers[0]} of ${photoPageCount}` : `Pages ${numbers[0]} and ${numbers[1]} of ${photoPageCount}`;
}

/** The CSS watermark on coloring pages. */
const Watermark = () => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
    <span className="-rotate-[30deg] whitespace-nowrap font-heading text-[17px] uppercase tracking-[0.25em] text-md-dark/15 md:text-[26px] lg:text-[21px]">
      Preview · Memory Doodle
    </span>
  </div>
);

const arrow = "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-md-control bg-white text-md-dark disabled:opacity-40 md:h-12 md:w-12 lg:h-11 lg:w-11";

const PageViewer = ({ pages = [], initialIndex = 0, onIndexChange = () => {}, className, children }) => {
  const [index, setIndex] = useState(Math.min(initialIndex, Math.max(0, pages.length - 1)));
  const stripRef = useRef(null);
  const swipeRef = useRef(null);
  const total = pages.length;
  const photoPageCount = pages.filter((p) => p.label?.startsWith("Page ")).length;
  const spreads = bookSpreads(total);
  const spreadAt = Math.max(0, spreads.findIndex((s) => s.includes(index)));
  const spread = spreads[spreadAt] || [];
  const spreadPages = spread.map((i) => pages[i]);

  useEffect(() => {
    if (index > total - 1) setIndex(Math.max(0, total - 1));
  }, [index, total]);

  // The parent can move the viewer (Preview opens on the first page that failed to draw).
  useEffect(() => {
    setIndex(Math.min(initialIndex, Math.max(0, total - 1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIndex]);

  useEffect(() => {
    onIndexChange(index);
    const el = stripRef.current?.querySelector(`[data-index="${index}"]`);
    el?.scrollIntoView?.({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [index, onIndexChange]);

  const goSpread = (s) => {
    const target = spreads[Math.min(spreads.length - 1, Math.max(0, s))];
    if (target) setIndex(target[0]);
  };
  const canBack = spreadAt > 0;
  const canForward = spreadAt < spreads.length - 1;

  if (!total) return null;

  const book = (
    <div
      data-testid="spread"
      className={classNames(
        "flex w-full overflow-hidden rounded-md shadow-[0_8px_24px_rgba(60,40,30,0.16)]",
        spread.length === 1 ? "max-w-[175px] md:max-w-[288px] lg:max-w-[228px]" : "max-w-[350px] md:max-w-[576px] lg:max-w-[456px]"
      )}
      onTouchStart={(e) => {
        swipeRef.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        const start = swipeRef.current;
        swipeRef.current = null;
        const end = e.changedTouches[0]?.clientX;
        if (start == null || end == null || Math.abs(end - start) < 40) return;
        goSpread(spreadAt + (end < start ? 1 : -1));
      }}
    >
      {spreadPages.map((page, i) => (
        <Page key={page.id} page={page} side={spread.length === 1 ? "right" : i === 0 ? "left" : "right"} />
      ))}
    </div>
  );

  return (
    <div className={classNames("flex flex-col gap-4", className)}>
      {/* One set of controls, arranged per device: phones and tablets put the spread on its own row
          with the arrows around "Pages 2 and 3 of N" below; desktop puts the arrows beside the spread
          on a beige panel (Desk5Preview). */}
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-4 md:gap-x-5 lg:flex-nowrap lg:gap-x-4 lg:rounded-[14px] lg:bg-[#F3EEE7] lg:px-4 lg:py-7">
        <div className="order-1 flex basis-full flex-col items-center gap-1 lg:order-2 lg:min-w-0 lg:flex-1 lg:basis-auto">
          {book}
          {spreadPages
            .filter((p) => p?.action?.below)
            .map((p) => (
              <PagePill key={p.id} action={p.action} />
            ))}
        </div>
        <button type="button" aria-label="Previous pages" onClick={() => goSpread(spreadAt - 1)} disabled={!canBack} className={classNames(arrow, "order-2 lg:order-1")}>
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <span className="order-3 min-w-[160px] text-center text-[15px] font-semibold text-md-dark md:text-base lg:hidden" aria-live="polite">
          {spreadLabel(spreadPages, photoPageCount)}
        </span>
        <button type="button" aria-label="Next pages" onClick={() => goSpread(spreadAt + 1)} disabled={!canForward} className={classNames(arrow, "order-4 lg:order-3")}>
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      {children}

      {/* Thumbnails grouped by spread. Centred when it fits, scrollable from the start when it does not. */}
      <div ref={stripRef} className="flex overflow-x-auto px-4 py-1 lg:px-0 [&>*:first-child]:lg:ml-auto [&>*:last-child]:lg:mr-auto" role="tablist" aria-label="Pages">
        {spreads.map((group, s) => (
          <div key={group.join("-")} className={classNames("flex flex-shrink-0 gap-0.5", s > 0 && "ml-3")}>
            {group.map((i) => {
              const page = pages[i];
              const selected = spread.includes(i);
              return (
                <button
                  key={page.id}
                  type="button"
                  role="tab"
                  data-index={i}
                  aria-selected={selected}
                  aria-label={`${page.label}${page.failed ? ", could not be drawn" : page.blank || page.ready ? "" : ", still drawing"}`}
                  onClick={() => setIndex(i)}
                  className={classNames(
                    "h-[58px] w-11 flex-shrink-0 overflow-hidden rounded-md bg-white",
                    page.failed ? "border-2 border-red-700 bg-red-50" : selected ? "border-2 border-md-pink" : "border border-md-control",
                    page.blank && !page.failed && !page.dedication && "bg-[#F6F1EA]",
                    page.dedication && "flex items-center justify-center text-md-pink-dark",
                    !page.ready && !page.blank && "animate-pulse bg-[#ECE5DC]"
                  )}
                >
                  {page.dedication ? <PenLine className="h-4 w-4" aria-hidden="true" /> : page.failed || page.blank ? null : page.ready && page.content ? page.content : page.ready && page.url ? <RetryImage src={page.url} alt="" className="h-full w-full object-cover" /> : null}
                </button>
              );
            })}
          </div>
        ))}
      </div>

    </div>
  );
};

export default PageViewer;
